import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Copy, ExternalLink, LoaderCircle, Plug } from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../components/layout/Layout.tsx';
import { fetchRestaurant, saveSettings, validateSaiposConnection } from '../services/api.ts';
import { getApiErrorMessage } from '../../../../utils/apiError';
import {
  SAIPOS_ACCOUNT_URL,
  SAIPOS_TUTORIAL_URL,
} from '../constants/saipos';

const getStoreCode = (data: any) => {
  const savedCode = String(data?.integration_saipos_cod_store || '').trim();
  if (savedCode) return savedCode;

  const restauranteId = String(data?.id || '').trim();
  return restauranteId ? `ATB-${restauranteId}` : '';
};

const SaiposIntegration = () => {
  const [active, setActive] = useState(false);
  const [idPartner, setIdPartner] = useState('');
  const [codStore, setCodStore] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [validationMessage, setValidationMessage] = useState('');

  const isInvalid = active && (!idPartner.trim() || !codStore.trim());

  const loadIntegration = useCallback(async () => {
    try {
      setIsLoading(true);

      const data = await fetchRestaurant();
      const nextCodStore = getStoreCode(data);

      setActive(!!data.integration_saipos_active);
      setIdPartner(data.integration_saipos_idpartner || '');
      setCodStore(nextCodStore);
    } catch (error) {
      console.error('Error fetching integration:', error);
      toast.error(getApiErrorMessage(error, 'Erro ao carregar configurações.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIntegration();
  }, [loadIntegration]);

  const handleIdPartnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIdPartner(e.target.value);
    setValidationStatus('idle');
    setValidationMessage('');
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setActive(e.target.checked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isInvalid) {
      toast.error('Informe o Id Partner para ativar a integração Saipos.');
      return;
    }

    const payload: any = {
      integration_saipos_active: active,
      integration_saipos_idpartner: idPartner.trim(),
      integration_saipos_cod_store: codStore.trim(),
    };

    try {
      setIsSaving(true);

      await saveSettings(payload);

      const updatedData = await fetchRestaurant();
      const persistedCodStore = String(updatedData.integration_saipos_cod_store || '').trim();

      setActive(!!updatedData.integration_saipos_active);
      setIdPartner(updatedData.integration_saipos_idpartner || '');
      setCodStore(getStoreCode(updatedData));

      if (payload.integration_saipos_cod_store && persistedCodStore !== payload.integration_saipos_cod_store) {
        toast.error('Configuração enviada, mas o Código da loja no AtendaBot salvo não confere. Recarregue a página e tente novamente.');
        return;
      }

      toast.success('Configurações salvas com sucesso.');
    } catch (error) {
      console.error('Error saving integration:', error);
      toast.error(getApiErrorMessage(error, 'Erro ao salvar configurações.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyStoreCode = async () => {
    if (!codStore) return;

    try {
      await navigator.clipboard.writeText(codStore);
      toast.success('Código da loja copiado.');
    } catch (error) {
      console.error('Error copying Saipos store code:', error);
      toast.error('Não foi possível copiar automaticamente. Selecione o código e copie manualmente.');
    }
  };

  const handleValidateConnection = async () => {
    const normalizedIdPartner = idPartner.trim();
    if (!normalizedIdPartner) {
      setValidationStatus('error');
      setValidationMessage('Informe o Id Partner antes de validar.');
      return;
    }

    try {
      setValidationStatus('loading');
      setValidationMessage('');
      const result = await validateSaiposConnection(normalizedIdPartner);
      setValidationStatus('success');
      setValidationMessage(result?.message || 'Conexão com a Saipos validada com sucesso.');
    } catch (error) {
      console.error('Error validating Saipos connection:', error);
      setValidationStatus('error');
      setValidationMessage(getApiErrorMessage(error, 'Erro ao validar conexão com a Saipos.'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-[1400px] space-y-5">
          <div className="mb-1 text-center">
            <h2 className="flex items-center justify-center gap-3 text-2xl font-black text-slate-900">
              <Plug size={28} className="text-[#2f7d72]" />
              Configurar integração Saipos
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-slate-600">
              Conecte sua loja Saipos para sincronizar pedidos automaticamente com o Atendabot.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-2xl border border-slate-200 p-4 shadow-sm sm:p-5">
            <div className="flex items-center">
              <span className="mr-2">Ativar</span>
              <label htmlFor="saipos-active" className="relative cursor-pointer">
                <input
                  id="saipos-active"
                  type="checkbox"
                  className="sr-only"
                  checked={active}
                  onChange={handleActiveChange}
                />
                <span className={`block w-10 h-6 rounded-full transition-colors duration-300 ${active ? 'bg-[#2f7d72]' : 'bg-gray-300'}`}></span>
                <span className={`absolute left-0 top-0 w-6 h-6 rounded-full bg-white shadow transform transition-transform duration-300 ${active ? 'translate-x-4' : ''}`}></span>
              </label>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">Antes de preencher</p>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Abra a Saipos, habilite a integração do Foodstation e informe nela o código da loja exibido abaixo. Depois de salvar, copie o Id Partner gerado pela Saipos e volte para esta tela.
              </p>
              <a
                href={SAIPOS_ACCOUNT_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-lg border border-[#2f7d72] bg-white px-4 text-sm font-semibold text-[#2f7d72] transition hover:bg-teal-50"
              >
                Abrir painel Saipos
                <ExternalLink size={16} />
              </a>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código da loja no AtendaBot</label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  value={codStore}
                  readOnly
                  aria-label="Código da loja no AtendaBot"
                  className="w-full rounded-md border border-gray-300 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-700 shadow-sm"
                />
                <button
                  type="button"
                  onClick={handleCopyStoreCode}
                  disabled={!codStore}
                  className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-teal-500 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Copy size={16} />
                  Copiar código
                </button>
              </div>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Copie este código e informe no campo CÓD. LOJA da integração do AtendaBot dentro da Saipos.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identificador da integração Saipos (Id Partner)</label>
              <input
                type="text"
                value={idPartner}
                onChange={handleIdPartnerChange}
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm"
              />
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Depois de salvar o canal: Saipos &gt; Menu &gt; Canais de venda e integrações &gt; Foodstation &gt; engrenagem &gt; Configurações &gt; Id Partner do parceiro.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleValidateConnection}
                  disabled={!idPartner.trim() || validationStatus === 'loading' || isSaving}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-[#2f7d72] bg-white px-4 text-sm font-semibold text-[#2f7d72] transition hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {validationStatus === 'loading' ? (
                    <LoaderCircle size={16} className="animate-spin" />
                  ) : validationStatus === 'success' ? (
                    <CheckCircle2 size={16} />
                  ) : (
                    <Plug size={16} />
                  )}
                  {validationStatus === 'loading' ? 'Validando...' : 'Validar conexão'}
                </button>
                {validationMessage ? (
                  <p
                    role="status"
                    className={`text-xs font-medium ${validationStatus === 'success' ? 'text-emerald-700' : 'text-rose-600'}`}
                  >
                    {validationMessage}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-4 text-sm font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? (
                  <div className="w-5 h-5 rounded-full animate-spin border-2 border-solid border-white border-t-transparent"></div>
                ) : (
                  'Salvar'
                )}
              </button>
            </div>
          </form>

          <section className="mx-auto w-full max-w-4xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" aria-labelledby="saipos-tutorial-title">
            <div className="mb-4">
              <h3 id="saipos-tutorial-title" className="text-base font-bold text-slate-900 sm:text-lg">
                Tutorial de integração com a Saipos
              </h3>
              <p className="mt-1 text-xs leading-5 text-slate-600 sm:text-sm">
                Assista ao passo a passo para habilitar o AtendaBot na Saipos e localizar o Id Partner.
              </p>
            </div>
            <iframe
              src={SAIPOS_TUTORIAL_URL}
              title="Tutorial de integração do AtendaBot com a Saipos"
              className="block w-full rounded-xl border border-slate-200 bg-black shadow-sm"
              style={{ aspectRatio: '16 / 9' }}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default SaiposIntegration;
